import React, { useState, useEffect } from 'react';
import { HardDrive, Link2, Unplug, CheckCircle, RefreshCw } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const GoogleDriveStatus = ({ compact = false }) => {
  const [connected, setConnected] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const res = await api.get('/google-drive/status');
      setConnected(res.data.connected);
      setEmail(res.data.email || '');
    } catch (err) {
      setConnected(false);
    }
    setLoading(false);
  };

  const handleConnect = async () => {
    try {
      const res = await api.get('/google-drive/connect');
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Drive?')) return;
    try {
      await api.delete('/google-drive/disconnect');
      setConnected(false);
      setEmail('');
      toast.success('Disconnected');
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${compact ? 'p-2' : 'p-4'} bg-white/5 rounded-xl`}>
        <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
        <span className="text-gray-400 text-sm">Checking...</span>
      </div>
    );
  }

  if (compact) {
    return connected ? (
      <div className="flex items-center gap-2 px-3 py-2 bg-green-500/20 text-green-400 rounded-lg">
        <HardDrive className="w-4 h-4" />
        <span className="text-sm truncate max-w-[120px]">{email}</span>
        <button
          onClick={handleDisconnect}
          className="ml-auto text-red-400 hover:text-red-300"
          title="Disconnect"
        >
          <Unplug className="w-4 h-4" />
        </button>
      </div>
    ) : (
      <button
        onClick={handleConnect}
        className="flex items-center gap-2 px-3 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
      >
        <HardDrive className="w-4 h-4" />
        <span className="text-sm">Connect Drive</span>
      </button>
    );
  }

  return (
    <div className="bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-xl p-4 border border-green-500/20">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
          <HardDrive className="w-5 h-5 text-green-400" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-white">Google Drive</h4>
          {connected ? (
            <p className="text-sm text-green-400 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Connected as {email}
            </p>
          ) : (
            <p className="text-sm text-gray-400">Not connected</p>
          )}
        </div>
        {connected ? (
          <button
            onClick={handleDisconnect}
            className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-2"
          >
            <Unplug className="w-4 h-4" />
            Disconnect
          </button>
        ) : (
          <button
            onClick={handleConnect}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
          >
            <Link2 className="w-4 h-4" />
            Connect
          </button>
        )}
      </div>
    </div>
  );
};

export default GoogleDriveStatus;
